import {
  DEFAULT_TEAM_PERMISSIONS,
  TEAM_PERMISSION_KEYS,
} from '@modules/merchant/team/constants/teamPermissions';

const toBoolean = (value) => value === true || value === 'true' || value === 1 || value === '1';

const toSnakeCase = (key) => key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const toCanKey = (key) => `can${key.charAt(0).toUpperCase()}${key.slice(1)}`;

const isPlainObject = (value) =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const hasOwn = (object, key) =>
  isPlainObject(object) && Object.prototype.hasOwnProperty.call(object, key);

const readDeclaredBoolean = (object, key) => {
  if (!isPlainObject(object)) return undefined;

  if (hasOwn(object, key)) return toBoolean(object[key]);

  const snake = toSnakeCase(key);
  if (hasOwn(object, snake)) return toBoolean(object[snake]);

  const canKey = toCanKey(key);
  if (hasOwn(object, canKey)) return toBoolean(object[canKey]);

  return undefined;
};

const sourceDeclaresTeamPermission = (source) => {
  if (!isPlainObject(source)) return false;

  const nested = source.permissions || source.userPermissions;
  if (
    isPlainObject(nested) &&
    TEAM_PERMISSION_KEYS.some((key) => typeof readDeclaredBoolean(nested, key) === 'boolean')
  ) {
    return true;
  }

  return TEAM_PERMISSION_KEYS.some((key) => typeof readDeclaredBoolean(source, key) === 'boolean');
};

/**
 * Reads a team permission from one source object.
 * Returns undefined when the source does not declare team permissions.
 */
export const readTeamPermission = (source, key) => {
  if (!isPlainObject(source)) return undefined;

  const nested = source.permissions || source.userPermissions;
  if (isPlainObject(nested) && sourceDeclaresTeamPermission({ permissions: nested })) {
    const nestedValue = readDeclaredBoolean(nested, key);
    // Permissions bag present → omitted keys are denied.
    return typeof nestedValue === 'boolean' ? nestedValue : false;
  }

  return readDeclaredBoolean(source, key);
};

/**
 * Resolves a team permission across multiple sources.
 * Defaults to true when no source declares the permission (owner / full access).
 */
export const resolveTeamPermission = (sources = [], key, defaultValue = true) => {
  for (const source of sources) {
    const value = readTeamPermission(source, key);
    if (typeof value === 'boolean') return value;
  }

  return defaultValue;
};

export const resolveTeamPermissions = (sources = [], defaultValue = true) =>
  TEAM_PERMISSION_KEYS.reduce(
    (permissions, key) => {
      permissions[key] = resolveTeamPermission(sources, key, defaultValue);
      return permissions;
    },
    { ...DEFAULT_TEAM_PERMISSIONS },
  );

/**
 * Only returns a permissions object when the API actually declared team permissions.
 */
export const extractExplicitTeamPermissions = (...sources) => {
  const declaredSources = sources.filter(sourceDeclaresTeamPermission);
  if (!declaredSources.length) return null;

  // When the API sends a permissions payload, omitted keys are false.
  return resolveTeamPermissions(declaredSources, false);
};

export default resolveTeamPermission;
